import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Box, GridItem, Image, Text, Tooltip } from '@chakra-ui/react';
import PropTypes from 'prop-types';

import IntegrationExternalIdStack from '../../../../components/IntegrationExternalIdStack';
import Row from '../../../../components/Row';
import RowGridColumn from '../../../../components/Row/components/RowGridColumn';
import { TruncatedTextCell } from '../../../../components/Text/TruncatedTextCell';
import UserIcon from '../../../../components/UserIcon';
import { analyticsIds } from '../../../../constants/analyticsIds';
import { ListJobsPageDuskIds } from '../../../../constants/duskIds';
import { Statuses } from '../../../../constants/statuses';
import colors from '../../../../styles/theme/foundation/colors';
import { getStatusDisplayName } from '../../../../utils/statusUtil';
import JobStatusBadge from '../JobStatusBadge';
import ProgressCircle from '../ProgressCircle';

const gridItemStyle = {
  height: '100%',
  fontWeight: '400'
};

const JobRow = ({ job }) => {
  const jobIntegrations = useMemo(() => job?.jobIntegrations ?? [], job);

  return (
    <Link
      data-analytics-id={analyticsIds.projectsListPage.selectJobRow}
      dusk={analyticsIds.projectsListPage.selectJobRow}
      style={{
        textDecoration: 'none',
        _hover: { textDecoration: 'none' }
      }}
      to={location => `${location.pathname}/${job.id}`}
    >
      <Row
        columns="repeat(16, 1fr)"
        height="82px"
        layerStyle={job.status === Statuses.Archived ? 'secondaryArchived' : 'row'}
        mt={4}
        textStyle="bodyMd"
      >
        <GridItem colSpan={4} pl={5} style={gridItemStyle}>
          <RowGridColumn align="flex-start" label="Project Name">
            <TruncatedTextCell
              duskSelector={ListJobsPageDuskIds.getJobRowByName(job.name)}
              maxLength={64}
              text={job.name}
            />
            {job.status === Statuses.Archived && (
              <JobStatusBadge
                badgeStyle={job.status}
                ml={4}
                status={getStatusDisplayName(job.status)}
              />
            )}
          </RowGridColumn>
        </GridItem>
        <GridItem colSpan={2} style={gridItemStyle}>
          <RowGridColumn label="Project Number">
            <TruncatedTextCell maxLength={18} text={job.number} />
          </RowGridColumn>
        </GridItem>
        <GridItem colSpan={2} style={gridItemStyle}>
          <RowGridColumn label="Project Owner">
            {job.owner
              ? (
                <UserIcon
                  bgColor={job.status === Statuses.Archived && '#7D8592'}
                  border="2px solid"
                  borderColor="white"
                  firstName={job.owner.firstName}
                  iconSize="35.28px"
                  key={job.owner.id}
                  lastName={job.owner.lastName}
                  ml={0}
                />
              )
              : (
                <Text>-</Text>
              )}

          </RowGridColumn>
        </GridItem>
        <GridItem colSpan={2} style={gridItemStyle}>
          <RowGridColumn label="Department">
            <Box
              border={`1px solid ${colors.greyScale['600']}`}
              borderRadius="4px"
              px="7px"
              py="3px"
            >
              <Text
                color="greyScale.600"
                dusk={ListJobsPageDuskIds.getJobDepartmentName(job.id)}
                textStyle="bodySm500"
                textTransform="capitalize"
              >
                {job.department.name}
              </Text>
            </Box>
          </RowGridColumn>
        </GridItem>
        <GridItem colSpan={2} style={gridItemStyle}>
          <RowGridColumn
            data-dusk-value={job.stats.activeInsureds}
            dusk={ListJobsPageDuskIds.getJobActiveRecords(job.id)}
            label="Active Insureds"
          >
            {job.stats.activeInsureds || '-'}
          </RowGridColumn>
        </GridItem>
        <GridItem colSpan={2} style={gridItemStyle}>
          <RowGridColumn
            data-dusk-value={job.stats.approvalPercentage}
            dusk={ListJobsPageDuskIds.getJobApprovalPercentage(job.id)}
            height="42px"
            justify="flex-start"
            label="Approved"
            width="42px"
          >
            <ProgressCircle percentage={job.status === Statuses.Archived ? null : job.stats.approvalPercentage} />
          </RowGridColumn>
        </GridItem>
        <GridItem colSpan={2} style={gridItemStyle}>
          <Text
            color={colors.greyScale['700']}
            mb={1}
            position="relative"
            textAlign="left"
            textStyle="bodySm500"
          >
            Project External Ids
          </Text>
          <IntegrationExternalIdStack
            customExternalId={job?.externalId}
            integrations={jobIntegrations}
            resourceName="jobId"
          />
        </GridItem>
      </Row>
    </Link>
  );
};

JobRow.propTypes = {
  job: PropTypes.shape({
    name: PropTypes.string,
    id: PropTypes.number,
    number: PropTypes.string,
    owner: PropTypes.shape({
      id: PropTypes.number,
      firstName: PropTypes.string,
      lastName: PropTypes.string
    }),
    department: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string
    }),
    stats: PropTypes.shape({
      activeInsureds: PropTypes.number,
      activeApprovedInsureds: PropTypes.number,
      approvalPercentage: PropTypes.number
    }),
    status: PropTypes.string
  })

};

export default JobRow;
